// Form field — label + input + optional error message. Mirrors
// src/components/common/FormField.tsx but with web-native input types and
// an error slot underneath.

import React from 'react'
import clsx from 'clsx'

interface FormFieldProps {
  label: string
  value: string
  onChange?: (value: string) => void
  placeholder?: string
  editable?: boolean
  /** Inline validation error displayed below the input. */
  error?: string | null
  /** HTML input type — text (default), number, etc.
   *  NOTE: `type="number"` is intentionally NOT recommended — it renders
   *  the browser's spinner buttons which we don't want. Use the
   *  `inputMode` prop instead to surface the right mobile keyboard while
   *  keeping the input visually clean. */
  type?: React.HTMLInputTypeAttribute
  /** Mobile keyboard hint without the spinner side-effect of type="number".
   *  Use 'numeric' for integers, 'decimal' for fractional values. */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  /** Optional ReactNode to render to the right of the label (e.g. a "Suggest" button). */
  labelAction?: React.ReactNode
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  editable = true,
  error,
  type = 'text',
  inputMode,
  labelAction,
}) => (
  <div className="flex flex-1 flex-col gap-xs">
    <div className="flex items-center justify-between">
      <label className="text-sm text-textSecondary">{label}</label>
      {labelAction}
    </div>
    <input
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder}
      readOnly={!editable}
      className={clsx(
        'h-12 rounded-md border bg-surfaceLight px-lg text-md text-textPrimary',
        'placeholder-textMuted outline-none transition-colors',
        error
          ? 'border-statusRed focus:border-statusRed'
          : 'border-surfaceBorder focus:border-primary',
        !editable && 'opacity-70',
      )}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
    />
    {error && <span className="text-xs text-statusRed">{error}</span>}
  </div>
)
